import {useEffect, useRef} from 'react';

type FocusIndexType = number | null;

const QUIET_TIME = 300;
const MAX_WAIT = 2000;
const FOLLOW_TIME = 1500;
const SCROLL_END_FALLBACK = 1000;

// events user can be using i.e. scrolling themselves
const USER_EVENTS = ['wheel', 'touchstart', 'keydown', 'mousedown'];

const getScrollParent = (element: Element): Element => {
    let parent = element.parentElement;

    while (parent) {
        const {overflowY} = getComputedStyle(parent);

        if (
            (overflowY == 'auto' || overflowY == 'scroll') &&
            parent.scrollHeight > parent.clientHeight
        ) {
            return parent;
        }

        parent = parent.parentElement;
    }

    return document.documentElement;
};

const isInView = (element: Element, scrollParent: Element) => {
    const {top, bottom} = element.getBoundingClientRect();
    const area =
        scrollParent == document.documentElement
            ? {top: 0, height: window.innerHeight}
            : scrollParent.getBoundingClientRect();
    const centre = (top + bottom) / 2;

    return (
        centre >= area.top + area.height / 4 &&
        centre <= area.top + (area.height * 3) / 4
    );
};

export default (
    scrollIndex: FocusIndexType,
    dataLength: number,
    options: ScrollIntoViewOptions = {}
) => {
    const elementsRef = useRef<HTMLDivElement[]>([]);

    const setRef = (element: HTMLDivElement, index: number) => {
        elementsRef.current[Number(index)] = element;
    };

    useEffect(() => {
        // a negative index means the row is not in the data yet, e.g. before api is resolved
        // and table is updated.
        if (
            !elementsRef ||
            scrollIndex == null ||
            scrollIndex < 0 ||
            dataLength <= 0 ||
            elementsRef.current.length !== dataLength
        ) {
            return;
        }

        const element = elementsRef.current.at(scrollIndex);

        if (!element) return;

        const scrollParent = getScrollParent(element);
        const scrollTarget: EventTarget =
            scrollParent == document.documentElement ? window : scrollParent;

        let phase: 'waiting' | 'scrolling' | 'following' = 'waiting';
        let quietTimer: ReturnType<typeof setTimeout>;
        let scrollEndTimer: ReturnType<typeof setTimeout>;
        let followTimer: ReturnType<typeof setTimeout>;

        const onScrollEnd = () => {
            if (phase != 'scrolling') return;

            clearTimeout(scrollEndTimer);
            scrollTarget.removeEventListener('scrollend', onScrollEnd);
            phase = 'following';

            if (!followTimer) followTimer = setTimeout(stop, FOLLOW_TIME);
        };

        const scrollToRow = () => {
            clearTimeout(quietTimer);
            clearTimeout(capTimer);
            phase = 'scrolling';

            element.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
                ...options,
            });

            scrollTarget.addEventListener('scrollend', onScrollEnd);
            scrollEndTimer = setTimeout(onScrollEnd, SCROLL_END_FALLBACK);
        };

        const observer = new ResizeObserver(() => {
            if (phase == 'waiting') {
                clearTimeout(quietTimer);
                quietTimer = setTimeout(scrollToRow, QUIET_TIME);
            } else if (
                phase == 'following' &&
                !isInView(element, scrollParent)
            ) {
                scrollToRow();
            }
        });
        Array.from(scrollParent.children).forEach((child) =>
            observer.observe(child)
        );

        const capTimer = setTimeout(scrollToRow, MAX_WAIT);

        const stop = () => {
            observer.disconnect();
            [quietTimer, capTimer, scrollEndTimer, followTimer].forEach(
                (timer) => clearTimeout(timer)
            );
            scrollTarget.removeEventListener('scrollend', onScrollEnd);
            USER_EVENTS.forEach((event) =>
                window.removeEventListener(event, stop)
            );
        };

        USER_EVENTS.forEach((event) =>
            window.addEventListener(event, stop, {passive: true})
        );

        return stop;
    }, [dataLength, elementsRef, scrollIndex]);

    return {
        elementsRef,
        setRef,
    };
};
